import { Fragment, forwardRef, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import { DestinationPropertyFragment } from "@/apollo/types";
import { Badge } from "@/components/elements/Badge";
import Tooltip from "@/components/elements/Tooltip";
import cn from "@/helpers/classNames";
import mergeRefs from "@/utils/mergeRefs";
import { DestinationPropertyOption } from "@/widgets/syncs/reverse-etl/state/StepStateContext";
import { Combobox, ComboboxProps, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";

type DestinationPropertySelectorProps = ComboboxProps<
  DestinationPropertyOption,
  true,
  false,
  "div"
>;

export const DestinationPropertySelector = forwardRef<
  HTMLInputElement,
  DestinationPropertySelectorProps & {
    allowCreate?: boolean;
    options: DestinationPropertyFragment[];
  }
>(({ allowCreate, options: optionsProp, ...props }, ref) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<any>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "bottom-end",
    modifiers: [
      { name: "preventOverflow", enabled: true },
      { name: "offset", options: { offset: [0, 4] } },
    ],
  });

  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { options, currentOption, showCreateOption } = useMemo(() => {
    const mappedOptions = optionsProp.map(
      (opt): DestinationPropertyOption => ({
        value: opt.propertyId,
        label: opt.propertyName,
        meta: opt,
      }),
    );

    let currentOption: DestinationPropertyOption | null = null;
    if (props.value) {
      currentOption =
        mappedOptions.find((x) => x.value === props.value!.value) || null;
      if (!currentOption) {
        currentOption = {
          isCustom: true,
          value: props.value.value,
          label: props.value.label || props.value.value,
        };
      }
    }

    if (currentOption) {
      mappedOptions.unshift(currentOption);
    }

    const filteredOptions =
      query === ""
        ? mappedOptions
        : mappedOptions.filter((option) => {
            const queryLower = query.toLowerCase();
            const name = option.label.toLowerCase();
            const id = option.value.toLowerCase();
            return name.includes(queryLower) || id.includes(queryLower);
          });

    return {
      options: filteredOptions,
      currentOption,
      showCreateOption: !mappedOptions.some(
        (x) => x.value.toLowerCase() === query.toLowerCase(),
      ),
    };
  }, [optionsProp, props.value, query]);

  const showCreate = allowCreate && query && showCreateOption;

  const forceOpenDropdown = () => {
    // The Combobox does not have a programmatic way to open the dropdown
    // We can force it by dispatching a keyboard event (alternatively, by simulating a click on the dropdown button)
    if (inputRef.current) {
      inputRef.current.focus();
      const event = new KeyboardEvent("keydown", {
        key: "ArrowDown",
        code: "ArrowDown",
        keyCode: 40,
        which: 40,
        bubbles: true,
      });
      inputRef.current.dispatchEvent(event);
    }
  };

  const handleChange = (value: DestinationPropertyOption) => {
    props.onChange?.(value);
  };

  function handleSelectAllText() {
    // Select all text in the input field
    if (inputRef.current) {
      inputRef.current.select();
    }
  }

  return (
    <Combobox<DestinationPropertyOption, "div">
      as="div"
      by="value"
      {...props}
      value={currentOption}
      onChange={handleChange}
      onClick={() => forceOpenDropdown()}
    >
      <div
        ref={setReferenceElement}
        className={cn(
          "relative flex h-10 w-full cursor-default items-center overflow-hidden rounded-sm border text-left text-sm focus-within:outline focus-within:outline-blue-600",
          {
            "cursor-not-allowed bg-muted": props.disabled,
          },
        )}
      >
        <Combobox.Input<DestinationPropertyOption | null>
          ref={mergeRefs(ref, inputRef)}
          displayValue={(option) => option?.label || ""}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onFocus={handleSelectAllText}
          onClick={handleSelectAllText}
          placeholder={
            allowCreate
              ? "Select or create a property..."
              : "Select a property..."
          }
          className="h-full w-full truncate border-none bg-transparent py-2 pl-4 pr-0 text-sm leading-5 text-foreground focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
        />
        {props.value?.meta && (
          <div className="flex items-center gap-1 px-1">
            {props.value.meta.readonly && (
              <Badge
                variant="secondary"
                className="px-0.5 py-0 uppercase leading-tight"
              >
                Readonly
              </Badge>
            )}
            <Badge
              variant="secondary"
              className="px-0.5 py-0 uppercase leading-tight"
            >
              {props.value.meta.propertyType}
            </Badge>
          </div>
        )}
        {props.value?.isCustom && (
          <Badge
            variant="quaternary"
            className="px-0.5 py-0 uppercase leading-tight"
          >
            Custom
          </Badge>
        )}
        <Combobox.Button className="ml-auto flex items-center pr-2">
          <ChevronUpDownIcon
            className="h-5 w-5 text-muted-foreground"
            aria-hidden="true"
          />
        </Combobox.Button>
      </div>
      {createPortal(
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery("")}
        >
          <Combobox.Options
            as="div"
            ref={(elm) => setPopperElement(elm as HTMLUListElement | null)}
            style={styles.popper}
            {...attributes.popper}
            className="absolute z-10 max-h-80 w-full max-w-md overflow-auto rounded border bg-background shadow-lg focus:outline-none dark:bg-gray-900"
          >
            <div className="py-1 text-sm">
              {options.length === 0 ? (
                <div className="relative mb-1 cursor-default select-none px-4 py-2 text-muted-foreground">
                  No destination properties available.
                </div>
              ) : null}
              <ul>
                {options.map((option) => (
                  <Combobox.Option
                    key={option.value}
                    value={option}
                    className={({ active, selected }) =>
                      cn(`relative cursor-default select-none px-4 py-2`, {
                        "bg-blue-500 text-white": active || selected,
                      })
                    }
                  >
                    {({ active, selected }) => (
                      <div>
                        <div className="flex gap-2">
                          <Tooltip content={option.label}>
                            <div
                              className={cn("truncate", {
                                "font-medium": selected,
                              })}
                            >
                              {option.label}
                            </div>
                          </Tooltip>
                          {option.meta && (
                            <div className="ml-auto flex gap-1">
                              {option.meta.readonly && (
                                <Badge
                                  variant="secondary"
                                  className="px-0.5 py-0 uppercase leading-tight"
                                >
                                  Readonly
                                </Badge>
                              )}
                              <Badge
                                variant="secondary"
                                className="px-0.5 py-0 uppercase leading-tight"
                              >
                                {option.meta.propertyType}
                              </Badge>
                            </div>
                          )}
                        </div>
                        <div
                          className={cn("text-xs text-muted-foreground", {
                            "text-white": active || selected,
                          })}
                        >
                          {option.value}
                        </div>
                      </div>
                    )}
                  </Combobox.Option>
                ))}
                {showCreate && (
                  <Combobox.Option<"li", DestinationPropertyOption>
                    value={{
                      value: query.trim(),
                      label: query.trim(),
                      isCustom: true,
                    }}
                    className={({ active, selected }) =>
                      cn(`relative cursor-default select-none px-4 py-2`, {
                        "bg-blue-500 text-white": active || selected,
                      })
                    }
                  >
                    Create "{query}"
                  </Combobox.Option>
                )}
              </ul>
            </div>
          </Combobox.Options>
        </Transition>,
        document.body,
      )}
    </Combobox>
  );
});
